import { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Trash2, Plus, Settings2, Lightbulb, History, Link2, FileText, Info, ArrowUpDown, Zap, AlertTriangle, CheckCircle, ShieldAlert } from 'lucide-react';
import { CaseCptLine, SurgeonTemplate, TemplateCptEntry } from '@/lib/templates/types';
import { MODIFIER_DESCRIPTIONS } from '@/lib/modifiers/types';
import { getSecondaryDefaultModifiers } from '@/lib/modifiers/xepsu';
import { AddProcedureDialog } from './AddProcedureDialog';
import { AddModifierDialog } from './AddModifierDialog';
import { RemoveProcedureDialog } from './RemoveProcedureDialog';
import { ApplyToTemplateDialog } from './ApplyToTemplateDialog';
import { useAddonSuggestions, recordCptUsage, type AddonSuggestion } from '@/hooks/useAddonSuggestions';
import { useCptRanking, type CptRankingResult } from '@/hooks/useCptRanking';
import type { CptEvidenceResult, EvidenceStatus } from '@/lib/cptEvidenceCrossRef';
import { getEvidenceResultsForDemoPresentation } from '@/lib/demo/perfectWorldDemo';

interface CodingPanelProps {
  cptLines: CaseCptLine[];
  template: SurgeonTemplate | null;
  commonAddons: TemplateCptEntry[];
  onRemoveLine: (code: string) => void;
  onAddLine: (line: CaseCptLine) => void;
  onToggleModifier: (code: string, modifier: string) => void;
  onTemplateUpdated?: () => void;
  surgeonId?: string;
  caseId?: string;
  payerPlanId?: string | null;
  facilityId?: string | null;
  /** Evidence results from CPT cross-reference engine */
  evidenceResults?: CptEvidenceResult[];
  /** Callback when surgeon manually overrides an unsupported code */
  onEvidenceOverride?: (code: string, reason: string) => void;
  /** Hide unsupported-warning chrome only in the frozen Perfect World presentation. */
  suppressUnsupportedEvidenceWarnings?: boolean;
}

const SOURCE_ICON: Record<string, typeof Lightbulb> = {
  template: FileText,
  cpt_relationship: Link2,
  historical: History,
};

const CONFIDENCE_COLORS: Record<string, string> = {
  high: 'bg-green-50 text-green-700 border-green-300',
  medium: 'bg-amber-50 text-amber-700 border-amber-300',
  low: 'bg-red-50 text-red-700 border-red-300',
};

const EVIDENCE_STATUS_CONFIG: Record<EvidenceStatus, { color: string; icon: typeof CheckCircle; label: string }> = {
  supported: { color: 'text-green-600', icon: CheckCircle, label: 'Evidence Found' },
  unsupported: { color: 'text-amber-600', icon: AlertTriangle, label: 'Missing Evidence' },
  override: { color: 'text-blue-600', icon: ShieldAlert, label: 'Manual Override' },
};

const cptCode = (line: Pick<CaseCptLine, 'code'>) => String(line.code);

export function CodingPanel({
  cptLines,
  template,
  commonAddons,
  onRemoveLine,
  onAddLine,
  onToggleModifier,
  onTemplateUpdated,
  surgeonId,
  caseId,
  payerPlanId,
  facilityId,
  evidenceResults,
  onEvidenceOverride,
  suppressUnsupportedEvidenceWarnings = false,
}: CodingPanelProps) {
  const [showAddProc, setShowAddProc] = useState(false);
  const [showAddMod, setShowAddMod] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<CaseCptLine | null>(null);

  const [templatePrompt, setTemplatePrompt] = useState<{
    line: CaseCptLine;
    changeType: 'procedure' | 'modifier';
    modifierCode?: string;
  } | null>(null);

  const visibleEvidenceResults = useMemo(() => (
    getEvidenceResultsForDemoPresentation(
      evidenceResults,
      suppressUnsupportedEvidenceWarnings,
    )
  ), [evidenceResults, suppressUnsupportedEvidenceWarnings]);

  // Build the presentation lookup without changing the underlying evidence truth.
  const evidenceMap = useMemo(() => {
    const map = new Map<string, CptEvidenceResult>();
    visibleEvidenceResults.forEach(result => map.set(result.code, result));
    return map;
  }, [visibleEvidenceResults]);

  const hasAnyUnsupported = useMemo(() => {
    return visibleEvidenceResults.some(result => result.status === 'unsupported');
  }, [visibleEvidenceResults]);

  // Exclude non-rankable codes (e.g., 09980 Surgical Assistant) from ranking
  const NON_RANKABLE_CODES = new Set(['09980']);
  const allCodes = cptLines.map(cptCode);
  const rankableCodes = allCodes.filter(c => !NON_RANKABLE_CODES.has(c));
  const existingCodes = new Set(allCodes);
  const primaryLines = cptLines.filter(l => l.section === 'primary');
  const primaryCodes = primaryLines.map(cptCode);

  // CPT Primary Ranking Engine (exclude assistant/non-procedure codes)
  const { ranking } = useCptRanking({
    cptCodes: rankableCodes,
    payerPlanId,
    surgeonId,
    facilityId,
    enabled: rankableCodes.length > 0,
  });

  // Merged suggestions from template, CPT relationships, and historical usage
  const { suggestions } = useAddonSuggestions({
    primaryCodes,
    existingCodes,
    templateAddons: commonAddons,
    surgeonId,
  });

  const handleAddSuggestion = (suggestion: AddonSuggestion) => {
    const newLine: CaseCptLine = {
      code: suggestion.code,
      description: suggestion.description,
      modifiers: getSecondaryDefaultModifiers(),
      section: 'secondary',
      fromTemplate: false,
    };
    onAddLine(newLine);

    if (surgeonId) {
      primaryCodes.forEach(pc => recordCptUsage(surgeonId, pc, suggestion.code));
    }

    if (template) {
      setTemplatePrompt({ line: newLine, changeType: 'procedure' });
    }
  };

  const handleProcedureAdded = (line: CaseCptLine) => {
    onAddLine(line);

    if (surgeonId && line.section === 'secondary') {
      primaryCodes.forEach(pc => recordCptUsage(surgeonId, pc, cptCode(line)));
    }

    if (template) {
      setTemplatePrompt({ line, changeType: 'procedure' });
    }
  };

  const handleModifierAdded = (line: CaseCptLine, modifier: string) => {
    if (template) {
      setTemplatePrompt({ line, changeType: 'modifier', modifierCode: modifier });
    }
  };

  // Use ranking to separate primary vs secondary display
  const rankedPrimary = ranking?.primary_cpt;
  const displayPrimaryLines = ranking
    ? cptLines.filter(l => cptCode(l) === rankedPrimary)
    : primaryLines;
  const displaySecondaryLines = ranking
    ? cptLines.filter(l => cptCode(l) !== rankedPrimary)
    : cptLines.filter(l => l.section === 'secondary');

  return (
    <div className="space-y-3">
      {/* Anti-Hallucination Gate Warning */}
      {hasAnyUnsupported && (
        <div className="flex items-start gap-2.5 p-3 rounded-lg border-2 border-amber-400 bg-amber-50/80">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-bold text-amber-800">Anti-Hallucination Gate Active</p>
            <p className="text-[11px] text-amber-700 mt-0.5 leading-relaxed">
              Codes flagged ⚠️ lack clinical evidence in the uploaded documents.
              They will be <strong>excluded from the AI narrative</strong> to prevent fabricated justifications.
              Remove or manually override below.
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="pb-2 px-4 pt-4">
          <CardTitle className="text-sm font-semibold flex items-center justify-between">
            Coding Panel
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" onClick={() => setShowAddMod(true)}>
                <Settings2 className="w-3.5 h-3.5 mr-1" />
                Add Modifier
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowAddProc(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add Procedure
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 space-y-3">
          {/* Primary Procedures */}
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <Badge variant="default" className="text-[9px] h-4 px-1.5 bg-primary/90">PRIMARY</Badge>
              Procedure{displayPrimaryLines.length !== 1 ? 's' : ''}
              {ranking && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    Primary selected based on payer/specialty ranking rules.
                    {ranking.reasons.map((r, i) => (
                      <div key={i} className="mt-0.5">• {r}</div>
                    ))}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {displayPrimaryLines.map(line => (
              <div key={cptCode(line)}>
                <CptLineItem
                  line={line}
                  onToggleModifier={onToggleModifier}
                  evidenceResult={evidenceMap.get(cptCode(line))}
                  onOverride={onEvidenceOverride}
                />
                {ranking?.ranking_source === 'pair_rule' && ranking.matched_pair_rule && (
                  <div className="flex items-center gap-1 ml-1 mb-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center gap-1 text-[10px] text-accent-foreground/70 bg-accent/50 rounded px-1.5 py-0.5 cursor-help">
                          <Zap className="w-2.5 h-2.5" />
                          Auto-ranked by CPT Pair Rule
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-xs text-xs">
                        {ranking.matched_pair_rule.reason}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                )}
              </div>
            ))}
            {displayPrimaryLines.length === 0 && (
              <p className="text-xs text-muted-foreground italic">No primary procedures</p>
            )}
          </div>

          <Separator />

          {/* Secondary / Ancillary */}
          <div>
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <Badge variant="secondary" className="text-[9px] h-4 px-1.5">SECONDARY</Badge>
              Ancillary
              <span className="text-[10px] font-normal">(XS default; RCM can refine to XE/XP/XU)</span>
            </div>
            {displaySecondaryLines.map(line => (
              <CptLineItem
                key={cptCode(line)}
                line={line}
                onToggleModifier={onToggleModifier}
                onRemove={() => setRemoveTarget(line)}
                evidenceResult={evidenceMap.get(cptCode(line))}
                onOverride={onEvidenceOverride}
              />
            ))}
            {displaySecondaryLines.length === 0 && (
              <p className="text-xs text-muted-foreground italic">No secondary procedures</p>
            )}
          </div>

          <Separator />

          {/* Precert Submission Order */}
          {ranking && ranking.ordered_cpts.length > 1 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <ArrowUpDown className="w-3 h-3" />
                Precert Submission Order
                <Badge variant="outline" className={`text-[9px] h-4 px-1.5 ${CONFIDENCE_COLORS[ranking.confidence]}`}>
                  {ranking.confidence}
                </Badge>
              </div>
              <div className="space-y-0.5">
                {ranking.ordered_cpts.map((code, idx) => {
                  const line = cptLines.find(l => cptCode(l) === code);
                  const ev = evidenceMap.get(code);
                  return (
                    <div key={code} className="flex items-center gap-2 text-xs py-0.5">
                      <span className="w-4 h-4 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold shrink-0">
                        {idx + 1}
                      </span>
                      <span className="font-mono font-medium">{code}</span>
                      <Badge variant={ranking.role_map[code] === 'primary' ? 'default' : 'secondary'} className="text-[9px] h-4 px-1">
                        {ranking.role_map[code]}
                      </Badge>
                      {ev && ev.status === 'unsupported' && (
                        <Badge variant="outline" className="text-[9px] h-4 px-1 border-amber-400 text-amber-600 bg-amber-50">
                          ⚠️ No Evidence
                        </Badge>
                      )}
                      <span className="text-muted-foreground truncate">{line?.description || ''}</span>
                    </div>
                  );
                })}
              </div>
              {!suppressUnsupportedEvidenceWarnings && ranking.warnings.length > 0 && (
                <div className="mt-1.5 space-y-0.5">
                  {ranking.warnings.map((w, i) => (
                    <p key={i} className="text-[10px] text-amber-600 dark:text-amber-400">⚠ {w}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Common Associated Add-Ons suggestions */}
      {suggestions.length > 0 && (
        <Card className="border border-muted">
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <Lightbulb className="w-3.5 h-3.5" />
              Common Associated Add-Ons
            </div>
            <p className="text-[10px] text-muted-foreground">
              Based on template patterns, code relationships, and your prior usage. Select only if clinically intended.
            </p>
            <div className="space-y-1">
              {suggestions.map(s => {
                const Icon = SOURCE_ICON[s.source] || Lightbulb;
                return (
                  <div
                    key={s.code}
                    className="flex items-center justify-between p-2 rounded-md border border-dashed hover:bg-accent/50 cursor-pointer group"
                    onClick={() => handleAddSuggestion(s)}
                  >
                    <div className="flex items-center gap-2 text-xs min-w-0">
                      <Icon className="w-3 h-3 text-muted-foreground shrink-0" />
                      <span className="font-mono font-medium shrink-0">{s.code}</span>
                      <span className="text-muted-foreground truncate">{s.description}</span>
                      <Badge variant="outline" className="text-[9px] h-4 shrink-0 border-muted-foreground/30">
                        {s.reason}
                      </Badge>
                    </div>
                    <Button variant="ghost" size="sm" className="h-6 text-xs shrink-0 opacity-0 group-hover:opacity-100">
                      <Plus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <AddProcedureDialog
        open={showAddProc}
        onOpenChange={setShowAddProc}
        onAdd={handleProcedureAdded}
        existingCodes={existingCodes}
        cptLines={cptLines}
        template={template}
      />

      <AddModifierDialog
        open={showAddMod}
        onOpenChange={setShowAddMod}
        cptLines={cptLines}
        onToggleModifier={onToggleModifier}
        onModifierAdded={handleModifierAdded}
      />

      <RemoveProcedureDialog
        line={removeTarget}
        onClose={() => setRemoveTarget(null)}
        onRemoveCaseOnly={(code) => {
          onRemoveLine(code);
          setRemoveTarget(null);
        }}
        onTemplateUpdated={onTemplateUpdated}
        templateId={template?.id}
        templateName={template?.template_name}
        caseId={caseId}
      />

      <ApplyToTemplateDialog
        open={!!templatePrompt}
        onOpenChange={(v) => { if (!v) setTemplatePrompt(null); }}
        line={templatePrompt?.line || null}
        templateId={template?.id}
        templateName={template?.template_name}
        onTemplateUpdated={onTemplateUpdated}
        changeType={templatePrompt?.changeType || 'procedure'}
        modifierCode={templatePrompt?.modifierCode}
        caseId={caseId}
      />
    </div>
  );
}

// Single CPT line item with traffic light evidence indicator
function CptLineItem({
  line,
  onToggleModifier,
  onRemove,
  evidenceResult,
  onOverride,
}: {
  line: CaseCptLine;
  onToggleModifier: (code: string, modifier: string) => void;
  onRemove?: () => void;
  evidenceResult?: CptEvidenceResult;
  onOverride?: (code: string, reason: string) => void;
}) {
  const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);

  const statusConfig = evidenceResult
    ? EVIDENCE_STATUS_CONFIG[evidenceResult.status]
    : null;

  const StatusIcon = statusConfig?.icon;

  return (
    <div className={`flex items-start justify-between py-1.5 group rounded-md px-1 ${
      evidenceResult?.status === 'unsupported' ? 'bg-amber-50/50 border border-amber-200/50' : ''
    }`}>
      <div className="space-y-0.5 flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Traffic Light Indicator */}
          {StatusIcon && evidenceResult && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={`shrink-0 ${statusConfig.color}`}>
                  <StatusIcon className="w-3.5 h-3.5" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs">
                <p className="font-semibold">{statusConfig.label}</p>
                {evidenceResult.evidenceSummary && (
                  <p className="mt-0.5 text-muted-foreground">{evidenceResult.evidenceSummary}</p>
                )}
                {evidenceResult.overrideReason && (
                  <p className="mt-0.5">Override: {evidenceResult.overrideReason}</p>
                )}
              </TooltipContent>
            </Tooltip>
          )}

          <span className="font-mono text-sm font-semibold">{cptCode(line)}</span>
          {line.modifiers.map(m => (
            <Badge
              key={m}
              variant="secondary"
              className="text-[10px] h-4 cursor-pointer hover:bg-destructive/20 max-w-[10rem] truncate"
              title={MODIFIER_DESCRIPTIONS[m] || m}
              onClick={() => onToggleModifier(cptCode(line), m)}
            >
              {m}
            </Badge>
          ))}

          {/* Unsupported badge */}
          {evidenceResult?.status === 'unsupported' && (
            <Badge variant="outline" className="text-[9px] h-4 px-1.5 border-amber-400 text-amber-700 bg-amber-50 gap-0.5">
              ⚠️ Missing Evidence
            </Badge>
          )}

          {/* Override badge */}
          {evidenceResult?.status === 'override' && (
            <Badge variant="outline" className="text-[9px] h-4 px-1.5 border-blue-400 text-blue-700 bg-blue-50 gap-0.5">
              🔓 Overridden
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{line.description}</p>

        {/* Inline actions for unsupported codes */}
        {evidenceResult?.status === 'unsupported' && (
          <div className="flex items-center gap-2 mt-1">
            {onRemove && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-[10px] px-2 text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={onRemove}
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Remove Code
              </Button>
            )}
            {onOverride && !showOverrideConfirm && (
              <Button
                variant="outline"
                size="sm"
                className="h-6 text-[10px] px-2 text-blue-600 border-blue-300 hover:bg-blue-50"
                onClick={() => setShowOverrideConfirm(true)}
              >
                <ShieldAlert className="w-3 h-3 mr-1" />
                Manual Override
              </Button>
            )}
            {showOverrideConfirm && onOverride && (
              <div className="flex items-center gap-1">
                <Button
                  variant="default"
                  size="sm"
                  className="h-6 text-[10px] px-2"
                  onClick={() => {
                    onOverride(cptCode(line), 'Surgeon attests clinical evidence exists for this procedure.');
                    setShowOverrideConfirm(false);
                  }}
                >
                  Confirm: I attest evidence exists
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-[10px] px-1.5"
                  onClick={() => setShowOverrideConfirm(false)}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {onRemove && evidenceResult?.status !== 'unsupported' && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 text-destructive"
          onClick={onRemove}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      )}
    </div>
  );
}
